import { useState } from 'react'

import { Modal } from '../Modal'
import {
  MANAGED_POLICY_ARNS,
  READONLY_NAME,
  WA_TOOL_PUBLISH_ACTIONS,
  WA_TOOL_PUBLISH_POLICY,
  WA_TOOL_PUBLISH_POLICY_NAME,
} from './iamSetup'

/**
 * "What access do I need?" — the one plain-language explanation of the AWS
 * permissions, shown above every connection method (internal workspace, the
 * standalone scan page and the public /newcustomer flow all render
 * AwsConnectionEditor, so they all get it).
 *
 * The per-method helpers (AwsIamSetupHelp / AwsOrgIamSetupHelp /
 * AwsAssumeRoleSetupHelp) say HOW to grant access; this says WHAT to grant and
 * WHY, per assessment type and account layout. Policy names come from
 * iamSetup.ts so the guide and the snippets can't disagree.
 */

const policyName = (arn: string) => arn.split('/').pop() ?? arn
const READ_POLICIES = MANAGED_POLICY_ARNS.map(policyName)

export function AwsAccessGuide() {
  const [open, setOpen] = useState(false)
  return (
    <div className="aws-access-guide" data-testid="aws-access-guide">
      <p style={{ margin: 0, fontSize: '0.9rem' }}>
        <strong>Read-only access is all we need.</strong> Grant the AWS-managed{' '}
        {READ_POLICIES.map((p, i) => (
          <span key={p}>
            {i > 0 && ' + '}
            <code>{p}</code>
          </span>
        ))}{' '}
        policies. We never change anything in your account.{' '}
        <button
          type="button"
          className="link-button"
          onClick={() => setOpen(true)}
          data-testid="aws-access-guide-open"
        >
          What access do I need? →
        </button>
      </p>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="What AWS access do I need?"
        testId="aws-access-guide-modal"
        maxWidth={760}
      >
        <section>
          <h3 style={{ marginTop: 0 }}>The baseline: read-only</h3>
          <p>
            Attach two AWS-managed policies to the user or role you connect:
          </p>
          <ul>
            <li>
              <code>ReadOnlyAccess</code>: lets us list and describe your resources (compute,
              databases, networking, security settings). It cannot create, change or delete
              anything.
            </li>
            <li>
              <code>AWSBillingReadOnlyAccess</code>: lets us read Cost Explorer, so the report
              shows your spend by service and region.
            </li>
          </ul>
          <p className="muted">
            These are standard AWS policies, maintained by AWS. If a call is ever denied, the scan
            keeps going and simply reports what it couldn't see.
          </p>
        </section>

        <section>
          <h3>By assessment type</h3>
          <table className="aws-access-guide__table" data-testid="aws-access-guide-matrix">
            <thead>
              <tr>
                <th>Assessment</th>
                <th>What we read</th>
                <th>Access</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <strong>AI Assessment</strong>
                </td>
                <td>
                  An inventory of your AWS estate (regions, compute, data stores, networking, AI
                  services in use) plus annual spend. It pre-fills the technical sections of the
                  assessment.
                </td>
                <td>Read-only (the two policies above)</td>
              </tr>
              <tr>
                <td>
                  <strong>Well-Architected Review (WAFR)</strong>
                </td>
                <td>
                  Evidence for each best-practice question: backups, encryption, logging, alarms,
                  IAM hygiene, scaling, cost controls. That's about 140 read-only calls across 50
                  services, all covered by <code>ReadOnlyAccess</code>.
                </td>
                <td>
                  Read-only. Optionally, a small extra grant lets us save the finished review into
                  your account's Well-Architected Tool (below).
                </td>
              </tr>
            </tbody>
          </table>
          <p className="muted" style={{ fontSize: '0.85rem' }}>
            Anything a WAFR can't read from your account isn't a blocker: that question is simply
            asked of you during the guided session instead. Trusted Advisor checks are only
            available on a Business or Enterprise support plan; without one they're skipped.
          </p>
        </section>

        <section>
          <h3>WAFR only, optional: saving the review to your Well-Architected Tool</h3>
          <p>
            This is the <strong>only</strong> time we write anything to your account, and only
            after the consultant explicitly approves it. We create a workload, record our notes on
            each question and cut a milestone in the AWS Well-Architected Tool, which is how the
            review is registered with AWS. It touches no other resources. Skip it and the review
            still completes; it just gets entered into the Well-Architected Tool by hand.
          </p>
          <p>
            To allow it, add this inline policy (we suggest naming it{' '}
            <code>{WA_TOOL_PUBLISH_POLICY_NAME}</code>) to the same user or role:
          </p>
          <pre data-testid="aws-access-guide-watool-policy">
            {WA_TOOL_PUBLISH_POLICY}
          </pre>
          <p className="muted" style={{ fontSize: '0.85rem' }}>
            Actions: {WA_TOOL_PUBLISH_ACTIONS.map((a) => a.replace('wellarchitected:', '')).join(', ')}.
          </p>
        </section>

        <section>
          <h3>If you have more than one AWS account</h3>
          <p>
            Connect every account that runs the workloads you want assessed. Pick whichever
            method fits how your accounts are organized:
          </p>
          <ul>
            <li>
              <strong>AWS Organization:</strong> one set of credentials in your
              <strong> management (billing) account</strong>, plus a read-only role named{' '}
              <code>{READONLY_NAME}</code> deployed to every member account. The setup helper
              deploys that role everywhere in one step with a StackSet.
            </li>
            <li>
              <strong>Cross-account role:</strong> create the read-only role in each account and
              let our AWS account assume it. No credentials are shared at all.
            </li>
            <li>
              <strong>IAM Identity Center (SSO)</strong> or <strong>access keys</strong>: add each
              account individually.
            </li>
          </ul>
          <p>
            <strong>Why the management account helps, and why it isn't enough on its own:</strong>{' '}
            it sees <em>billing for the whole organization</em>, so spend is complete. But AWS
            keeps each account's resources private to that account, so the management account
            cannot list a member account's servers, databases or security settings. That's why
            organization mode also needs the read-only role in every member account. For a WAFR,
            connect at least the account (or accounts) that host the workload under review.
          </p>
        </section>

        <section>
          <h3>Security</h3>
          <ul className="muted" style={{ fontSize: '0.9rem' }}>
            <li>Access keys are encrypted at rest; the cross-account role option stores none.</li>
            <li>
              Prefer a dedicated user or role (we suggest <code>{READONLY_NAME}</code>) so you can
              revoke our access at any time by deleting it.
            </li>
            <li>
              Temporary credentials (SSO, or keys starting with <code>ASIA</code>) expire after a
              few hours; long-lived keys or the role option keep re-scans working.
            </li>
          </ul>
        </section>
      </Modal>
    </div>
  )
}
